// <pos-settings/> — 系統設定主頁（owner only）
// 子選單導向各設定子頁 + 本頁直接顯示 public_ordering_enabled 開關

import { apiUrl } from '../lib/api-base.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

class PosSettings extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      loading: true,
      error: null,
      tenant: null,
      updating: false,
    };
  }

  async connectedCallback() {
    this.render();
    await this.load();
  }

  async authHeaders() {
    const { getSupabase } = await import('../lib/supabase-client.js');
    const { data } = await getSupabase().auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async load() {
    try {
      const headers = await this.authHeaders();
      const res = await fetch(apiUrl('/.netlify/functions/tenant-settings'), { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message_zh || '讀取失敗');
      this.state.tenant = data.tenant;
    } catch (err) {
      this.state.error = err.message;
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  async togglePublicOrdering() {
    if (this.state.updating) return;
    const next = !this.state.tenant?.public_ordering_enabled;
    this.state.updating = true;
    this.render();
    try {
      const headers = await this.authHeaders();
      const res = await fetch(apiUrl('/.netlify/functions/tenant-settings'), {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_ordering_enabled: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message_zh || '更新失敗');
      this.state.tenant = data.tenant;
    } catch (err) {
      alert(`✗ ${err.message}`);
    } finally {
      this.state.updating = false;
      this.render();
    }
  }

  render() {
    const { loading, error, tenant, updating } = this.state;

    const publicEnabled = tenant?.public_ordering_enabled;
    const slug = tenant?.slug;
    const publicOrderSampleUrl = slug ? `${location.origin}/#/order/${encodeURIComponent(slug)}/1` : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-6) var(--s-8); max-width: 920px; margin: 0 auto; font-family: var(--font-sans); color: var(--fg-base); }
        h2 { font-size: 1.4rem; margin-bottom: var(--s-5); }
        .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); margin-bottom: var(--s-4); }
        .banner.err { background: rgba(239,68,68,.1); color: var(--danger); }
        .banner.loading { background: rgba(59,130,246,.1); color: var(--fg-muted); }
        .group { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); padding: var(--s-5); margin-bottom: var(--s-4); }
        .group h3 { font-size: 1.05rem; margin: 0 0 var(--s-3); color: var(--fg-base); }
        .row { display: flex; justify-content: space-between; align-items: center; padding: var(--s-3) 0; border-bottom: 1px dashed var(--border); }
        .row:last-child { border-bottom: 0; }
        .row__label { font-weight: 500; }
        .row__desc { font-size: .8rem; color: var(--fg-muted); margin-top: var(--s-1); line-height: 1.5; }
        .toggle { width: 52px; height: 28px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--r-full); position: relative; cursor: pointer; transition: background var(--dur-fast) var(--ease); flex-shrink: 0; }
        .toggle__knob { position: absolute; top: 2px; left: 2px; width: 22px; height: 22px; background: var(--fg-muted); border-radius: var(--r-full); transition: all var(--dur-fast) var(--ease); }
        .toggle.is-on { background: linear-gradient(135deg, var(--brand-primary), var(--brand-accent)); border-color: transparent; }
        .toggle.is-on .toggle__knob { left: 26px; background: white; }
        .toggle.is-disabled { opacity: .5; cursor: not-allowed; }
        .code { font-family: var(--font-mono); font-size: .85rem; background: var(--bg-surface); padding: var(--s-2) var(--s-3); border-radius: var(--r-sm); border: 1px solid var(--border); color: var(--brand-primary); word-break: break-all; }
        nav ul { list-style: none; padding: 0; margin: 0; }
        nav li { padding: 0; }
        nav a { display: flex; align-items: center; justify-content: space-between; padding: var(--s-3) var(--s-4); color: var(--fg-base); text-decoration: none; border-radius: var(--r-md); transition: background var(--dur-fast) var(--ease); }
        nav a:hover { background: var(--bg-surface); color: var(--brand-primary); }
        nav a::after { content: '›'; color: var(--fg-dim); font-size: 1.3rem; }
        .group--danger { border-color: var(--danger, #ef4444); }
        .group--danger h3 { color: var(--danger, #ef4444); }
        .btn-danger { background: transparent; color: var(--danger, #ef4444); border: 1px solid var(--danger, #ef4444); border-radius: var(--r-md); padding: var(--s-2) var(--s-4); cursor: pointer; font-family: inherit; flex-shrink: 0; }
        .btn-danger:hover { background: var(--danger, #ef4444); color: white; }
      </style>

      <h2>系統設定</h2>

      ${loading ? '<div class="banner loading">載入中…</div>' : ''}
      ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}

      ${tenant ? `
        <div class="group">
          <h3>店家基本</h3>
          <div class="row">
            <div>
              <div class="row__label">店家名稱</div>
              <div class="row__desc">${escapeHtml(tenant.display_name)}</div>
            </div>
          </div>
          <div class="row">
            <div>
              <div class="row__label">店家代稱</div>
              <div class="row__desc">${escapeHtml(tenant.slug)}（不可更改，用於 QR 網址）</div>
            </div>
          </div>
          <div class="row">
            <div>
              <div class="row__label">訂閱方案</div>
              <div class="row__desc">${escapeHtml(tenant.plan)}</div>
            </div>
          </div>
        </div>

        <div class="group">
          <h3>🆕 QR 顧客點餐</h3>
          <div class="row">
            <div style="flex:1">
              <div class="row__label">啟用線上點餐</div>
              <div class="row__desc">
                開啟後客人可掃 QR 直接在自己手機點餐，訂單會出現在訂單管理頁。<br>
                ${publicEnabled ? `範例公開網址：<span class="code">${escapeHtml(publicOrderSampleUrl)}</span>` : '關閉狀態，掃 QR 會顯示「此店家尚未開放線上點餐」'}
              </div>
            </div>
            <div class="toggle ${publicEnabled ? 'is-on' : ''} ${updating ? 'is-disabled' : ''}" data-act="toggle-public-ordering">
              <div class="toggle__knob"></div>
            </div>
          </div>
        </div>

        <div class="group">
          <h3>🏁 開始做生意</h3>
          <nav><ul>
            <li><a href="#/settings/payment">💰 收款帳號設定（綠界 / LINE Pay 申請教學）</a></li>
            <li><a href="#/menu">菜單編輯器</a></li>
            <li><a href="#/settings/qr">桌位 QR Code（客人掃碼點餐用）</a></li>
            <li><a href="#/settings/reservation">訂位設定（用餐時長 / 併桌組合）</a></li>
          </ul></nav>
        </div>

        <div class="group">
          <h3>👥 團隊與營運</h3>
          <nav><ul>
            <li><a href="#/staff">員工管理</a></li>
            <li><a href="#/settings/scheduling">排班設定（員工 / 班別 / 排休）</a></li>
            <li><a href="#/inventory">庫存管理</a></li>
            <li><a href="#/settings/workflow">訂單流程設定</a></li>
          </ul></nav>
        </div>

        <div class="group">
          <h3>⚙️ 進階與系統</h3>
          <nav><ul>
            <li><a href="#/settings/stations">出單站點與通知（多站點才需要）</a></li>
            <li><a href="#/settings/ai-keys">AI 金鑰設定（進階・選填）</a></li>
            <li><a href="#/settings/lighthouse">🏮 燈塔店家終身優惠兌換</a></li>
          </ul></nav>
        </div>

        <div class="group">
          <h3>💬 需要幫忙？</h3>
          <nav><ul>
            <li><a href="#/xiaohong">問問小掌櫃（AI 店長助理，隨時回答）</a></li>
            <li><a href="mailto:pat.peichen.lin@gmail.com">寫信給真人客服</a></li>
          </ul></nav>
        </div>

        <div class="group group--danger">
          <h3>帳號管理</h3>
          <div class="row">
            <div style="flex:1">
              <div class="row__label">刪除帳號</div>
              <div class="row__desc">
                永久刪除你的登入帳號與個人資料，無法復原。${tenant ? '你是店家擁有人：店面會一併停業下線（訂單與帳務紀錄依法保留、去識別化）。' : ''}
              </div>
            </div>
            <button class="btn-danger" data-act="delete-account">刪除帳號</button>
          </div>
        </div>
      ` : ''}
    `;

    this.wireEvents();
  }

  async deleteAccount() {
    const word = prompt('此操作無法復原。\n\n請輸入「刪除」二字以確認永久刪除帳號：');
    if (word === null) return;
    if (word.trim() !== '刪除') { alert('輸入不符，已取消'); return; }
    try {
      const headers = await this.authHeaders();
      const res = await fetch(apiUrl('/.netlify/functions/account-delete'), {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: '刪除' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error?.message_zh || '刪除失敗');
      alert('帳號已刪除。感謝你使用掌收。');
      const { getSupabase } = await import('../lib/supabase-client.js');
      await getSupabase().auth.signOut().catch(() => {});
      location.hash = '#/login';
      location.reload();
    } catch (err) {
      alert(`✗ ${err.message}`);
    }
  }

  wireEvents() {
    this.shadowRoot.querySelectorAll('[data-act]').forEach(el => {
      el.addEventListener('click', () => {
        if (el.dataset.act === 'toggle-public-ordering') this.togglePublicOrdering();
        if (el.dataset.act === 'delete-account') this.deleteAccount();
      });
    });
  }
}

customElements.define('pos-settings', PosSettings);
